import { EventrDate, eventrDateTimeDifference } from "@eventr/common";
import { differenceInDays, parseISO } from "date-fns";
import { DateTime } from "luxon";
import { useCallback } from "react";
import { useShallow } from "zustand/react/shallow";
import { useBoundStore } from "../../state/bound.store";
import { useLanguageContext } from "../../state/languageState/languageContext";
import { useUserPrefsStore } from "../../state/userPrefsState/userPrefs.store";
import { capitalizeFirstLetter } from "../../utils";

export const useFormatValue = () => {
  const { getLanguageISO639Code, timeFormat } = useLanguageContext(
    useShallow((s) => ({
      getLanguageISO639Code: s.getLanguageISO639Code,
      timeFormat: s.timeFormat,
    })),
  );
  // `formatValue` is called during the render of its consumers (e.g. `<Text />`), so this has to
  // be a subscribing selector read here rather than a `getState()` read inside the callback.
  const showDestTime = useUserPrefsStore((s) => s.showDestTime);
  const isPMAM = timeFormat === "h:mma";

  const formatValue = useCallback(
    (value: string, formatter: string, extraValues?: string[]) => {
      let parsedValue;

      try {
        if (value.startsWith("[") || value.startsWith("{")) {
          parsedValue = JSON.parse(value);
        } else {
          parsedValue = value;
        }
      } catch {
        parsedValue = value;
      }
      if (parsedValue === null) {
        return "";
      }

      if (typeof parsedValue === "number" && formatter === "terminal") {
        try {
          if (value.length === 1) {
            return "Terminal " + value.toString();
          } else {
            return value;
          }
        } catch {
          return value;
        }
      }
      if (typeof parsedValue === "string") {
        if (formatter.startsWith("TF;")) {
          try {
            const format = formatter.split(";")[1];
            if (format === undefined) return value;
            const parsedDate = new EventrDate(value);
            return parsedDate.itinLocalLuxon.toFormat(format);
          } catch {
            return value;
          }
        }
        switch (formatter) {
          case "date":
            if (value) {
              try {
                const parsedDate = new EventrDate(value);
                switch (showDestTime) {
                  case "userLocal":
                    return parsedDate.userLocalLuxon
                      .setLocale(getLanguageISO639Code())
                      .toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
                  case "itinLocal":
                    return parsedDate.itinLocalLuxon
                      .setLocale(getLanguageISO639Code())
                      .toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
                  case "utc":
                    return parsedDate.utcLuxon
                      .setLocale(getLanguageISO639Code())
                      .toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
                  default:
                    return parsedDate.itinLocalLuxon
                      .setLocale(getLanguageISO639Code())
                      .toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
                }
              } catch {
                return "";
              }
            } else {
              return "";
            }
          case "time":
          case "time2":
            try {
              if (!value) {
                return "";
              }
              const parsedDate = new EventrDate(value);
              switch (showDestTime) {
                case "userLocal":
                  return parsedDate.userLocalLuxon.toFormat(timeFormat);
                case "itinLocal":
                  return parsedDate.itinLocalLuxon.toFormat(timeFormat);
                case "utc":
                  return parsedDate.utcLuxon.toFormat(timeFormat);
                default:
                  return parsedDate.itinLocalLuxon.toFormat(timeFormat);
              }
            } catch {
              return "";
            }
          case "datetime":
            try {
              const parsedDate = new EventrDate(value);
              const format: Intl.DateTimeFormatOptions = {
                ...DateTime.DATETIME_MED,
                hourCycle: isPMAM ? "h12" : "h23",
              };

              switch (showDestTime) {
                case "userLocal":
                  return parsedDate.userLocalLuxon.setLocale(getLanguageISO639Code()).toLocaleString(format);
                case "itinLocal":
                  return parsedDate.itinLocalLuxon.setLocale(getLanguageISO639Code()).toLocaleString(format);
                case "utc":
                  return parsedDate.utcLuxon.setLocale(getLanguageISO639Code()).toLocaleString(format);
                default:
                  return parsedDate.itinLocalLuxon.setLocale(getLanguageISO639Code()).toLocaleString(format);
              }
            } catch {
              return "";
            }
          case "timeDifference":
            try {
              if (extraValues?.[0] === undefined || value === undefined) {
                return "";
              } else {
                const date1 = new EventrDate(extraValues?.[0]);
                const date2 = new EventrDate(value);
                const formattedDifference = eventrDateTimeDifference(date1, date2);

                return formattedDifference;
              }
            } catch {
              return "";
            }
          case "timeDifferenceV2":
            try {
              if (extraValues?.length === 2) {
                if (extraValues[1] !== null && extraValues !== undefined && extraValues[1] !== "") {
                  return extraValues[1];
                }
              }
              if (extraValues?.[0] === undefined || value === undefined) {
                return "";
              } else {
                const date1 = new EventrDate(extraValues?.[0]);
                const date2 = new EventrDate(value);
                const formattedDifference = eventrDateTimeDifference(date1, date2);

                return formattedDifference;
              }
            } catch {
              return "";
            }

          case "nightDifference":
            try {
              const days = differenceInDays(parseISO(extraValues?.[0] ?? ""), parseISO(value));
              if (Number.isNaN(days)) {
                return "";
              }
              if (days > 1 || days === 0) {
                return days + " Nights";
              } else {
                return days + " Night";
              }
            } catch {
              return "";
            }

          case "webLink":
            return "Website";

          case "estimated":
            try {
              if (extraValues) {
                if (
                  extraValues[0] !== "" &&
                  extraValues[0] !== null &&
                  extraValues[0] !== undefined &&
                  extraValues[0] !== " "
                ) {
                  if (extraValues[0] !== value && value) {
                    const parsedDate = new EventrDate(value);

                    const parsedExtraDate = new EventrDate(extraValues[0]);

                    const time = parsedDate.toItinLocalTimeString() + " -> " + parsedExtraDate.toItinLocalTimeString();

                    return time;
                  } else {
                    const parsedDate = new EventrDate(value);
                    return parsedDate.toItinLocalTimeString();
                  }
                } else {
                  const parsedDate = new EventrDate(value);
                  return parsedDate.toItinLocalTimeString();
                }
              } else {
                const parsedDate = new EventrDate(value);
                return parsedDate.toItinLocalTimeString();
              }
            } catch {
              // Error
              return "";
            }
          case "terminal":
            try {
              if (value.length === 1) {
                return "Terminal " + value;
              } else {
                return value;
              }
            } catch {
              return value;
            }

          default:
            if (extraValues) {
              if (value) {
                return [value].concat(extraValues.filter((str) => str !== "")).join(", ");
              } else {
                return extraValues
                  .filter((str) => str !== "")

                  .join(", ");
              }
            } else {
              return value;
            }
        }
      }
      if (parsedValue && parsedValue.option && parsedValue.type) {
        switch (formatter) {
          case "userOrTag":
            try {
              if (parsedValue.type === "User") {
                const user = useBoundStore.getState().getUserFromID(parseInt(parsedValue.option));
                return capitalizeFirstLetter(user?.firstName ?? "") + " " + capitalizeFirstLetter(user?.lastName ?? "");
              } else {
                return parsedValue.option;
              }
            } catch {
              return parsedValue.option;
            }
          default:
            return parsedValue.option;
        }
      }
      return value;
    },
    [getLanguageISO639Code, isPMAM, showDestTime, timeFormat],
  );

  /**
   * Formats a date value into a localized string. Does not care about the showDestTime preference
   * @param {string | Date | EventrDate} value - The date value to format.
   * @returns {string} - The formatted date string.
   */
  const formatDateTime = useCallback(
    (value: string | Date | EventrDate | number) => {
      try {
        let valueToUse;
        if (value instanceof EventrDate) {
          valueToUse = value.getUnixMs;
        } else if (value instanceof Date) {
          valueToUse = value.getTime();
        } else if (typeof value === "string") {
          valueToUse = new Date(value).getTime();
        } else {
          valueToUse = value;
        }

        const localizedDate = DateTime.fromMillis(valueToUse).toLocal();
        const format: Intl.DateTimeFormatOptions = {
          ...DateTime.DATETIME_MED,
          hourCycle: isPMAM ? "h12" : "h23",
        };

        return localizedDate.setLocale(getLanguageISO639Code()).toLocaleString(format);
      } catch {
        return "";
      }
    },
    [getLanguageISO639Code, isPMAM],
  );
  return { formatValue, formatDateTime };
};
